import { ref, onBeforeUnmount } from 'vue'
import { fetchEventSource } from '@microsoft/fetch-event-source'

import { getAccessToken } from '@/helpers/auth-helper'

import { useAuthorizationStore } from '@/store/authorizationStore'
import { useDefaultStore } from '@/store/defaultStore'

const getWorkspaceId = () => useAuthorizationStore().getWorkspaceSlug

export function useAgentStream() {
    const isStreaming = ref(false)

    const model = ref(null)
    const answer = ref('')
    const script = ref('')
    const usage = ref(null)
    const responseId = ref(null)
    const conversationId = ref(null)
    const status = ref(null)
    const errorMessage = ref(null)

    let controller = null

    function stop() {
        if (controller) {
            controller.abort()
            controller = null
        }
        isStreaming.value = false
    }

    function resetLocalState(keepConversation) {
        model.value = null
        answer.value = ''
        script.value = ''
        usage.value = null
        responseId.value = null
        status.value = null
        errorMessage.value = null

        if (!keepConversation) {
            conversationId.value = null
        }
    }

    function clearContext() {
        stop()
        resetLocalState(false)
    }

    function clearOutputOnly() {
        stop()
        resetLocalState(true)
    }

    function start({ prompt, url }) {
        stop()

        clearOutputOnly()

        isStreaming.value = true
        controller = new AbortController()

        const body = {
            conversation_id: conversationId.value ? conversationId.value : '',
            user_input: prompt,
        }

        fetchEventSource(`${import.meta.env.VITE_API_URL}/api/${getWorkspaceId()}${url}`, {
            method: 'POST',
            signal: controller.signal,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                Authorization: `Bearer ${getAccessToken()}`,
            },
            body: JSON.stringify(body),

            async onopen(response) {
                const contentType = response.headers.get('content-type') || ''
                if (!response.ok) {
                    const text = await response.text().catch(() => '')
                    throw new Error(`HTTP ${response.status} ${text}`)
                }

                if (contentType.includes('application/json')) {
                    const data = await response.clone().json().catch(() => null)

                    if (!data.success) {
                        const defaultStore = useDefaultStore()
                        defaultStore.setErrorMessage({ message: data.message })

                        stop()
                    }
                }

                if (!contentType.includes('text/event-stream')) {
                    stop()

                    throw new Error(`Expected text/event-stream, got: ${contentType}`)
                }
            },

            onmessage(message) {
                let data
                try {
                    data = JSON.parse(message.data)
                } catch {
                    data = {}
                }

                switch (message.event) {
                case 'error':
                    if (data.conversation_id !== undefined && data.conversation_id !== null) {
                        conversationId.value = data.conversation_id
                    }

                    errorMessage.value = data.message
                    break

                case 'started':
                    model.value = data.model || null
                    break

                case 'delta':
                    if (typeof data.delta === 'string') {
                        answer.value += data.delta
                    }
                    break

                case 'completed':
                    script.value = data.script || ''
                    usage.value = data.usage || null
                    responseId.value = data.response_id || null

                    if (data.conversation_id !== undefined && data.conversation_id !== null) {
                        conversationId.value = data.conversation_id
                    }
                    break

                case 'done':
                    stop()
                    break
                }
            },

            onerror(err) {
                status.value = `Stream error: ${String(err?.message || err)}`
                stop()
            },

            onclose() {
                isStreaming.value = false
            },
        }).catch((e) => {
            if (e && e.name === 'AbortError') {
                return
            }

            status.value = `Failed: ${String(e?.message || e)}`
            isStreaming.value = false
        })
    }

    onBeforeUnmount(stop)

    return {
        isStreaming,
        model,
        answer,
        script,
        usage,
        responseId,
        conversationId,
        status,
        errorMessage,

        start,
        stop,
        clearContext,
        clearOutputOnly,
    }
}
